import * as React from "react";
import { cn } from "@/lib/utils";
import { Eye, EyeSlash, Icon } from "@phosphor-icons/react";

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size"> {
  size?: "sm" | "md";
  icon?: Icon;
  wrapperClassName?: string;
  invalid?: boolean;
  showHidePassword?: boolean;
  severity?: "default" | "light";
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size,
      invalid,
      wrapperClassName,
      showHidePassword,
      severity = "default",
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    return (
      <div className={cn("relative", wrapperClassName)}>
        <input
          autoComplete="off"
          type={showPassword ? "text" : type}
          className={cn(
            "flex h-[43px] w-full rounded-md border-t px-[15px] py-1 text-base font-normal tracking-wide backdrop-blur-lg transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:font-normal focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            className,
            severity === "default" &&
              "border-[#1FB3CC]/15 bg-[#1FB3CC]/15 text-white shadow-[inset_0px_1px_2px_0px_#0C0C2633] placeholder:text-white/50 autofill:border-[#1FB3CC]/15 autofill:bg-[#1FB3CC]/15 autofill:text-white focus:bg-[#1FB3CC]/40 autofill:focus:bg-[#1FB3CC]/40",
            severity === "light" &&
              "border-white bg-white text-neutral-900 placeholder:text-neutral-500",
            size === "sm" && "h-8 px-3 text-sm placeholder:text-sm",
            invalid &&
              "!border border-[#E86363] bg-[#E86363]/15 text-white/80 focus:bg-[#E86363]/15"
          )}
          ref={ref}
          {...props}
          onChange={(e) => {
            if (props.onChange) {
              const value = e.target.value;

              if (type === "number" && value !== "") {
                const parsedValue = parseFloat(value);
                if (!isNaN(parsedValue)) {
                  if (value.includes(",") || value.includes(".")) {
                    e.target.value = value;
                  } else {
                    e.target.value = parsedValue.toString();
                  }
                } else {
                  e.target.value = value;
                }
              }

              props.onChange(e);
            }
          }}
        />
        {props.icon && !showHidePassword && (
          <props.icon
            className="absolute right-4 top-1/2 -translate-y-1/2"
            size={20}
            color={invalid ? "#ff7979" : "#719BAD"}
          />
        )}

        {showHidePassword && (
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2"
            type="button"
            onClick={() => setShowPassword(!showPassword)}
          >
            {!showPassword ? (
              <Eye size={20} color={invalid ? "#ff7979" : "#719BAD"} />
            ) : (
              <EyeSlash size={20} color={invalid ? "#ff7979" : "#719BAD"} />
            )}
          </button>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
